import React from 'react';
import { observer } from 'mobx-react';
import { Icon, IconName } from '@canvas-logic/clp-shared-ui';
import statusSendingNothingImg from '../assets/status-sending-nothing.svg';
import statusSendingLoadingImg from '../assets/status-sending-loading.svg';
import statusErrorImg from '../assets/status-error.svg';
import statusSuccessImg from '../assets/status-success.svg';
import styles from './StyleGuideIcons.module.css';

/** Icons in the order shown in Figma panel (node-id: 367:5832) */
const SYSTEM_ICONS: IconName[] = [
  'arrowLeftCircle',
  'arrowRightCircle',
  'arrowLeft',
  'arrowRight',
  'close',
  'closeCircle',
  'closeFab',
  'help',
  'info',
  'infoActive',
  'plus',
  'plusCircle',
  'minus',
  'minusCircle',
  'collapse',
  'expand',
  'expandViewport',
  'chevronDown',
  'chevronUp',
  'chevronLeft',
  'chevronRight',
  'more',
  'refreshCw',
  'search',
  'zoomOut',
  'zoomIn',
  'camera',
  'lock',
  'settings',
  'share',
  'link',
  'pdf',
  'ar',
  'shoppingBag',
  'star',
  'type24',
  'placeholder',
  'color5',
  'color3',
  'color2',
  'color1',
];

const STATUS_ICONS = [
  { label: 'sending(nothing)', src: statusSendingNothingImg },
  { label: 'sending(loading)', src: statusSendingLoadingImg },
  { label: 'error', src: statusErrorImg },
  { label: 'success', src: statusSuccessImg },
];

/** Icons that exist in the project but are not shown in the Figma panel */
const ADDITIONAL_ICONS: IconName[] = ['spinner', 'check', 'palette', 'arrow', 'asset', 'rotate', 'parent', 'child'];

/**
 * Icons page — mirrors Figma "icons / System" (node-id: 367:5832).
 */
export const StyleGuideIcons: React.FC = observer(function StyleGuideIcons() {
  return (
    <div className={styles.section}>
      {/* 24px System Icons */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>24</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>System Icons</h3>
        <div className={styles.demo}>
          <div className={styles.iconGrid}>
            {SYSTEM_ICONS.map((name) => (
              <div key={name} className={styles.iconCell}>
                <div className={styles.iconPreview}>
                  <Icon type={name} />
                </div>
                <span className={styles.iconLabel}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 128px Status Icons */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>128</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Status Icons</h3>
        <div className={styles.demo}>
          <div className={styles.statusGrid}>
            {STATUS_ICONS.map(({ label, src }) => (
              <div key={label} className={styles.statusCell}>
                <img className={styles.statusImage} src={src} alt={label} />
                <span className={styles.statusLabel}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Project Icons (extras — not in Figma) */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Additional Project Icons</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Icons in codebase but not in Figma</h3>
        <div className={styles.demo}>
          <div className={styles.iconGrid}>
            {ADDITIONAL_ICONS.map((name) => (
              <div key={name} className={styles.iconCell}>
                <div className={styles.iconPreview}>
                  <Icon type={name} />
                </div>
                <span className={styles.iconLabel}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
